/**
 * Idle-time warm-up for 3D sections further down the page.
 *
 * A scene that only starts loading when you scroll near it shows up late on a phone: its
 * code, its assets and its CPU setup all land at once, right as it comes into view. Once
 * the intro has finished, these helpers do that work while the browser is idle instead.
 *
 * @module
 */
import { useEffect, useRef } from 'react'
import { useAppStore } from '@/lib/store'
import { beginBackgroundWork } from './backgroundWork'
import { withRetry } from './retry'

type Connection = { saveData?: boolean; effectiveType?: string }

/** Save-Data or a 2G link: don't spend the visitor's data on sections they may never reach. */
export function isConstrainedConnection(): boolean {
  const connection = (navigator as Navigator & { connection?: Connection }).connection
  return Boolean(connection?.saveData) || /(^|-)2g$/.test(connection?.effectiveType ?? '')
}

/**
 * Runs `task` when the browser is idle (or after `timeout` ms at the latest). Returns a
 * cancel function. Falls back to a short timeout where `requestIdleCallback` is missing.
 */
export function whenIdle(task: () => void, timeout = 4000): () => void {
  if (typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(task, { timeout })
    return () => window.cancelIdleCallback(handle)
  }
  const handle = window.setTimeout(task, 250)
  return () => window.clearTimeout(handle)
}

/**
 * Once the page is `ready` and idle, runs `warm` once: download a scene's code, precompute
 * its data, fetch its model. Failures are ignored, because the scene loads (with retries)
 * on its own when it mounts. Pass a module-level function so it keeps one identity.
 */
export function usePrefetchAfterIntro(warm: () => Promise<unknown>): void {
  const ready = useAppStore((state) => state.phase === 'ready')
  const done = useRef(false)
  useEffect(() => {
    if (!ready || done.current || isConstrainedConnection()) return
    return whenIdle(() => {
      done.current = true
      const end = beginBackgroundWork()
      void withRetry(warm, { retries: 1 })
        .catch(() => undefined)
        .finally(end)
    })
  }, [ready, warm])
}
